import type {
  CmmdCloudProjectLifecyclePreflight,
  CmmdRuntimeDiagnosticsSnapshot,
  RuntimeDiagnosticsResponse,
  ServerTraceDiagnosticsResult,
} from "@cmmd-forge/contracts";

import { DiagnosticsCanonicalRuntimeSnapshot } from "./DiagnosticsCanonicalRuntimeSnapshot";
import type { CloudRuntimeView } from "./DiagnosticsCloudRuntimes.logic";
import { DiagnosticsLifecyclePreflight } from "./DiagnosticsLifecyclePreflight";
import { DiagnosticsMachineInventory } from "./DiagnosticsMachineInventory";
import { DiagnosticsPreparedContract } from "./DiagnosticsPreparedContract";
import { DiagnosticsTraceFailures } from "./DiagnosticsTraceFailures";
import type { PreparedContractRow } from "./preparedContractDiff";

/** Everything the trace section needs, which the cloud branch only relays. */
export interface CloudTraceDiagnostics {
  readonly result: ServerTraceDiagnosticsResult | undefined;
  readonly error: string | undefined;
  readonly pending: boolean;
  readonly unsupportedReason: string | undefined;
}

function DiagnosticsCloudTraceFailures(props: {
  readonly traces: CloudTraceDiagnostics;
  readonly probed: boolean;
}) {
  if (!props.probed) return null;
  return (
    <DiagnosticsTraceFailures
      result={props.traces.result}
      error={props.traces.error}
      pending={props.traces.pending}
      unsupportedReason={props.traces.unsupportedReason}
    />
  );
}

export function DiagnosticsCloudAdminEvidence(props: {
  readonly selectedRuntime: CloudRuntimeView;
  readonly machineInventory:
    | {
        readonly inventory: RuntimeDiagnosticsResponse | undefined;
        readonly error: string | undefined;
        readonly pending: boolean;
        readonly probed: boolean;
      }
    | undefined;
  readonly traces: CloudTraceDiagnostics;
  readonly contractRows: ReadonlyArray<PreparedContractRow>;
  readonly boundSnapshot: CmmdRuntimeDiagnosticsSnapshot | undefined;
  readonly canonicalError: string | undefined;
  readonly canonicalPending: boolean;
  readonly lifecycleReceipt: CmmdCloudProjectLifecyclePreflight | undefined;
  readonly lifecycleError: string | undefined;
  readonly lifecyclePending: boolean;
}) {
  const probed = props.machineInventory?.probed === true;
  const pending = props.machineInventory?.pending === true;
  return (
    <>
      <DiagnosticsMachineInventory
        inventory={props.machineInventory?.inventory}
        error={props.machineInventory?.error}
        pending={pending}
        probed={probed}
      />
      <DiagnosticsCloudTraceFailures traces={props.traces} probed={probed} />
      <DiagnosticsPreparedContract rows={props.contractRows} probed={probed} />
      <DiagnosticsCanonicalRuntimeSnapshot
        snapshot={props.boundSnapshot}
        error={props.canonicalError}
        pending={props.canonicalPending}
      />
      <DiagnosticsLifecyclePreflight
        runtime={props.selectedRuntime}
        receipt={props.lifecycleReceipt}
        error={props.lifecycleError}
        pending={props.lifecyclePending}
      />
    </>
  );
}
